import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useAgpQuery } from "../hooks/useAgpApi";
import { AttainmentBar, PaceChip, agpWindowLabel, formatTarget } from "./AgpUi";
import { useSources } from "../../hooks/useBdApi";
import { Card, Skeleton } from "../../components/Ui";
import SourceTip, { TitleWithSource } from "../../components/SourceTip";
import { HorizontalBars } from "../../components/Charts";
import { POINT_TYPES, formatNumber } from "../../metrics";

// AGP team key for the Client Acquisition / SGA (RevOps) practice (bdMetrics
// PRACTICE_ALIASES "Client Acquisition" -> "Client Acquisition Team").
export const CA_TEAM_KEY = "client-acquisition-team";

// AGP drivers shown for the practice, in display order: the RevOps workbook's
// sales goal (weighted points, signed clients by line, new business value)
// then the pipeline / funnel drivers. Keys unknown to the catalog are ignored
// by the API; drivers without a target are not returned.
const CA_DRIVER_KEYS = [
  "newBusinessPoints",
  "signedClients",
  "ibSignedClients",
  "evSignedClients",
  "newBusinessRevenue",
  "bdLeads",
  "ibLeads",
  "evLeads",
  "stage3Validated",
  "mqls",
  "scopeAdded",
  "outboundEmails",
];

// Point types with a "call happened" date, shown as booked -> held.
const CALL_TYPES = new Set(["TAP", "Client Meeting", "LMV"]);

function DriverTip({ driverKey, label }) {
  const { sources } = useSources();
  const metric = sources && sources.agpDrivers ? sources.agpDrivers[driverKey] : null;
  if (!metric) return null;
  return <SourceTip metric={metric} title={`AGP - ${label}`} />;
}

function GoalsEmpty({ to }) {
  return (
    <div className="bd-ca__empty">
      <strong>No AGP goals loaded for Client Acquisition for this year.</strong>
      <span className="bd-muted">Seed them from the RevOps workbook (scripts/data/bdAgp2026.json → seedBdAgp2026.js) or enter targets on the practice page.</span>
      <Link to={to} className="bd-link">
        Open the practice's AGP page →
      </Link>
    </div>
  );
}

function GoalRows({ data, to }) {
  if (!data.drivers.length) {
    return (
      <div className="bd-ca__empty">
        <span className="bd-muted">Goals exist but none of the sales / funnel drivers have a target yet.</span>
        <Link to={to} className="bd-link">
          Set targets →
        </Link>
      </div>
    );
  }
  return (
    <div className="bd-agp-strip__items bd-ca__goals">
      {data.drivers.map((d) => {
        const noActual = d.actual === null || d.actual === undefined;
        return (
          <Link key={d.key} to={to} className="bd-agp-strip__item">
            <div className="bd-agp-strip__label">
              <span>
                {d.label} <DriverTip driverKey={d.key} label={d.label} />
              </span>
              <PaceChip row={d} compact />
            </div>
            <div className="bd-agp-strip__value">
              <strong>{noActual ? <span className="bd-muted">—</span> : formatTarget(d.actual, d.format)}</strong>
              <span className="bd-muted"> / {formatTarget(d.target, d.format)}</span>
            </div>
            <AttainmentBar row={d} />
            {noActual && d.source === "manual" && <small className="bd-muted bd-ca__note">Manual driver · no entries yet</small>}
          </Link>
        );
      })}
    </div>
  );
}

// The practice's BD Workplan points this year, by type. A point belongs to
// the practice by its Landmark Team (the point's Team formula), so the
// Client Acquisition team's LMVs, TAPs and New Client Signings show up here
// like any other practice's points - and feed the auto drivers above (BD
// Leads = LMV points).
function WorkplanPoints({ points, to }) {
  const byType = (points && points.byType) || {};
  const taken = (points && points.takenByType) || {};
  const rows = POINT_TYPES.map((t) => ({
    key: t.type,
    name: t.label,
    value: byType[t.type] || 0,
    color: t.color,
    held: CALL_TYPES.has(t.type) ? taken[t.type] || 0 : null,
  })).filter((r) => r.value > 0);
  const total = rows.reduce((s, r) => s + r.value, 0);
  if (!total) {
    return <p className="bd-muted bd-ca__empty">No BD Workplan points attributed to the Client Acquisition team this year.</p>;
  }
  return (
    <>
      <HorizontalBars rows={rows} />
      <dl className="bd-ca__points">
        {rows.map((r) => (
          <div key={r.key}>
            <dt>
              <i style={{ background: r.color }} /> {r.name}
            </dt>
            <dd>
              {formatNumber(r.value)}
              {r.held !== null && <small className="bd-muted"> · {formatNumber(r.held)} held</small>}
            </dd>
          </div>
        ))}
        <div className="bd-ca__points-total">
          <dt>All points</dt>
          <dd>{formatNumber(total)}</dd>
        </div>
      </dl>
      <p className="bd-muted bd-ca__objective">
        Per-member breakdown on the <Link to={to} className="bd-link">practice page</Link>.
      </p>
    </>
  );
}

/**
 * Client Acquisition / SGA section for the AGP overview: the practice's AGP
 * goals (annual, Jan 1 -> today) next to its BD Workplan points for the year.
 */
export default function ClientAcquisitionSection({ year, search = "" }) {
  const params = useMemo(() => ({ year, team: CA_TEAM_KEY, keys: CA_DRIVER_KEYS.join(",") }), [year]);
  const { data: agp, loading } = useAgpQuery("summary", params);
  const hasGoals = !!(agp && agp.hasGoals);
  const to = `/landmark/bd/agp/team/${CA_TEAM_KEY}${search}`;

  return (
    <section className="bd-section bd-ca">
      <div className="bd-section-title">
        <h2>
          <TitleWithSource
            title="Client Acquisition / SGA"
            reports={["newClients", "closedWon", "wpPoints"]}
            how="The Client Acquisition (RevOps) team sells on behalf of every practice, so its sales drivers are measured org-wide: signed clients are Closed Won 'Client Acquisition - BD' opportunities and new business revenue is the summed Amount of all closed-won BD opportunities, from any practice. Its Workplan points (LMVs, TAPs, New Client Signings) are attributed by each point's Landmark Team, like the other practices; BD Leads = LMV points booked. Goals are annual (Jan 1 to today)."
            attribution="Team: the point's Landmark Team (Salesforce Team formula) for points; org-wide for sales drivers. Member: the point's Accredited To user / the Opportunity Owner."
          >
            Client Acquisition / SGA
          </TitleWithSource>
        </h2>
        <span className="bd-section-title__meta">
          <Link to={to} className="bd-link">
            Open practice goals →
          </Link>
        </span>
      </div>

      <div className="bd-grid bd-grid--2-1 bd-ca__grid">
        <Card
          className="bd-ca__agp"
          title={`AGP ${year} goals`}
          subtitle={
            <>
              {agp && agp.window ? agpWindowLabel(agp.window) : "Jan 1 – today"}
              {hasGoals && agp.orgScopedSales ? " · sales drivers counted org-wide" : ""}
            </>
          }
        >
          {loading && !agp ? (
            <div className="bd-agp-strip__items">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} height={72} radius={10} />
              ))}
            </div>
          ) : !hasGoals ? (
            <GoalsEmpty to={to} />
          ) : (
            <GoalRows data={agp} to={to} />
          )}
          {hasGoals && agp.coreObjective ? (
            <p className="bd-muted bd-ca__objective">
              <strong>Core objective:</strong> {agp.coreObjective}
            </p>
          ) : null}
        </Card>

        <Card
          title={
            <TitleWithSource
              title="Workplan points"
              reports={["wpPoints"]}
              how={`BD Workplan Points booked from Jan 1 ${year} to today whose Landmark Team is the Client Acquisition team: LMVs and New Client Signings follow the Accredited To user's team, TAPs the 'TAP Booked For' user's team, Client Meetings the client's practice. "Held" counts points whose call has happened (Call Taken On).`}
              attribution="Team: the point's Landmark Team (Team formula). Member: Accredited To."
            >
              Workplan points
            </TitleWithSource>
          }
          subtitle={`Client Acquisition team · ${year} YTD`}
          className="bd-ca__period"
        >
          {loading && !agp ? <Skeleton height={180} /> : <WorkplanPoints points={agp ? agp.points : null} to={to} />}
        </Card>
      </div>
    </section>
  );
}
